import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { Booking } from "../types/booking.entity";
import { useCountBookingsQuery, useGetBookingsQuery, BookingsFilter } from "../bookings.service";
import BookingsTable from "../components/bookings-table";
import BookingsSearch from "../components/bookings-search";
import { BookingsTableSkeleton } from "../components/BookingsTableSkeleton";
import { useCallback } from "react";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

const BookingsPage = () => {
  const tableSearch = usePaginatedTableSearchParams<BookingsFilter>({
    parseState: (searchParams) => {
      const query: BookingsFilter = {};
      const learnerId = searchParams.get("learnerId");
      const coachId = searchParams.get("coachId");
      const bookingId = searchParams.get("bookingId");
      const slotId = searchParams.get("slotId");
      const bookingStatus = searchParams.get("bookingStatus");
      const coachType = searchParams.get("coachType");
      const langCode = searchParams.get("langCode");

      if (learnerId) query.learnerId = learnerId;
      if (coachId) query.coachId = coachId;
      if (bookingId) query.bookingId = bookingId;
      if (slotId) query.slotId = slotId;
      if (bookingStatus) query.bookingStatus = bookingStatus as BookingsFilter["bookingStatus"];
      if (coachType) query.coachType = coachType as BookingsFilter["coachType"];
      if (langCode) query.langCode = langCode as BookingsFilter["langCode"];

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.learnerId) nextParams.set("learnerId", state.learnerId);
      if (state.coachId) nextParams.set("coachId", state.coachId);
      if (state.bookingId) nextParams.set("bookingId", state.bookingId);
      if (state.slotId) nextParams.set("slotId", state.slotId);
      if (state.bookingStatus) nextParams.set("bookingStatus", state.bookingStatus);
      if (state.coachType) nextParams.set("coachType", state.coachType);
      if (state.langCode) nextParams.set("langCode", state.langCode);
      return nextParams;
    },
  });

  const initialQuery = tableSearch.state;

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<Booking & { id: string }>({
      useGetDataQuery: useGetBookingsQuery,
      useCountData: useCountBookingsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const updateQuery = useCallback((newQuery: BookingsFilter) => {
    tableSearch.replaceFilters(newQuery);
  }, [tableSearch]);

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Bookings" />
      <div className="mb-4">
        <BookingsSearch updateQuery={updateQuery} initialQuery={initialQuery} />
      </div>
      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
              <table className="min-w-[1200px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
                <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
                  <tr>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Booking</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Participants</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Coach Type</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Created</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Date</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Slot</th>
                    <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <BookingsTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <BookingsTable data={data ?? []} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default BookingsPage;
